import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, InternalStandardProps } from "@mui/material";
import { styled } from "@mui/material/styles";

import { config } from "JS/Config";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppTypography } from "JS/React/Components/AppTypography";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useUserSavedDocIds } from "JS/React/Hooks/Database/Document";
import {
    useDocumentsDetails,
    useGlobalDocuments,
} from "JS/React/Hooks/Documents";
import { useRouting } from "JS/React/Hooks/Routes";

const StyledRoot = styled("div")({});

const StyledItemContainer = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(1, 0),
    padding: theme.spacing(1),
    "&:hover": {
        background:
            theme.palette.mode === "light"
                ? theme.palette.grey["200"]
                : theme.palette.grey[900],
        cursor: "pointer",
    },
}));

const StyledIcons = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "35px",
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

const StyledDescription = styled(AppTypography)(({ theme }) => ({
    fontSize: "30px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: theme.palette.grey[600],
    [theme.breakpoints.down("md")]: {
        fontSize: "20px",
    },
    [theme.breakpoints.down("sm")]: {
        fontSize: "18px",
    },
}));

const StyledPdfIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    color: "red",
    height: "35px",
    margin: theme.spacing(0, 2),
    fontSize: "35px",
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

const StyledRightIcons = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(0, 1),
    color: theme.palette.common.black,
}));

const StyledFloppyIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "26px",
    paddingTop: "2px",
    color: theme.palette.grey[600],
}));

const StyledStarIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "35px",
    color: theme.palette.grey[600],
    [theme.breakpoints.down("md")]: {
        fontSize: "22px",
    },
}));

export interface DocumentsListProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const DocumentsList = (props: DocumentsListProps) => {
    const { className, ...rest } = props;

    const navigate = useNavigate();
    const { linkProvider } = useRouting();

    const { documentUserFavorites, downloadedContentNids } =
        useGlobalDocuments();

    const { userDownloadedDocIds } = useUserSavedDocIds();

    const { docDetails, loading: docDetailsLoading } = useDocumentsDetails();

    const onDocumentClick = (pdfName: string, description?: string) => {
        navigate(
            linkProvider.react.documents().detail(pdfName, description),
        );
    };

    const isFavDocument = (name: string) =>
        documentUserFavorites?.findIndex((x) => x.Name === name) !== -1;

    const isDownloadedDoc = (pdf: string) =>
        userDownloadedDocIds?.includes(`${config.user.memberId}-${pdf}`) ||
        downloadedContentNids?.includes(`${config.user.memberId}-${pdf}`);

    return (
        <StyledRoot {...rest} className={className}>
            {docDetailsLoading && (
                <AppBackdropProgress
                    open={true}
                    backdropText={messaging.loader.documents}
                />
            )}

            {docDetails?.map((d, idx) => (
                <StyledItemContainer
                    key={d.Name + idx}
                    container
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    onClick={() => onDocumentClick(d.Name, d.Description)}
                >
                    <Grid
                        item
                        sm={9}
                        xs={9}
                        display={"flex"}
                        alignItems={"center"}
                    >
                        <StyledPdfIcon icon={["fas", "file-pdf"]} />
                        <StyledDescription variant="h4">
                            {d.Description}
                        </StyledDescription>
                    </Grid>
                    <Grid
                        item
                        sm={3}
                        xs={3}
                        display={"flex"}
                        justifyContent={"space-between"}
                        alignItems={"center"}
                    >
                        <StyledRightIcons item sm={1} xs={1}>
                            {isDownloadedDoc(d.Name) && (
                                <StyledFloppyIcon icon={["far", "floppy-disk"]} />
                            )}
                        </StyledRightIcons>
                        <StyledRightIcons item sm={1} xs={1}>
                            {isFavDocument(d.Name) && (
                                <StyledStarIcon icon={["fas", "star"]} />
                            )}
                        </StyledRightIcons>
                        <StyledRightIcons item sm={1} xs={1}>
                            <StyledIcons icon={["fas", "chevron-right"]} />
                        </StyledRightIcons>
                    </Grid>
                </StyledItemContainer>
            ))}
        </StyledRoot>
    );
};
